import React, { createContext, useContext, useMemo } from 'react';
import { Auth } from 'aws-amplify';
import { isValidEmail } from '@buddy-technology/buddy_helpers';
import { saveAs } from 'file-saver';
import omit from 'lodash/omit';

import CustomerHelper from '../util/CustomerHelper';
import createFetch from '../util/createFetch';
import getHasFilledOutContact from '../util/getHasFilledOutContactInfo';

import {
	DOWNLOAD_TYPE,
	CUSTOMER_PROPS_TO_OMIT,
} from './SimpleModels';
import getCarrierAndProducerIds from '../util/getCarrierAndProducerIds';
import getCarrierIdFromAuthData from '../util/getCarrierIdFromAuthData';

const API_URL = process.env.CLIENT_API_URL;

export const ApiContext = createContext();

export const ApiProvider = ({ children, authData, setAuthData }) => {
	const refreshToken = async () => {
		try {
			const { accessToken } = await Auth.currentSession();
			// cache token in parent state.
			const updatedAuthData = {
				...authData,
				signInUserSession: { ...authData.signInUserSession, accessToken },
			};
			setAuthData(updatedAuthData);
			return accessToken.jwtToken;
		} catch (error) {
			setAuthData(authData);
			return Promise.reject(error);
		}
	};

	const getToken = async () => {
		const accessToken = authData?.signInUserSession?.accessToken;
		const expirationTime = accessToken?.payload?.exp;
		const nowInSeconds = Math.round(Date.now() / 1000);
		const isTokenExpired = nowInSeconds >= expirationTime;
		if (accessToken && !isTokenExpired) {
			return accessToken.jwtToken;
		}
		try {
			const token = await refreshToken();
			return token;
		} catch (error) {
			return Promise.reject(error);
		}
	};

	// Helper function for calling "normal" API routes that return data in same format.
	const callCoreApi = async ({
		url, method = 'GET', body = null,
	}) => {
		try {
			const token = await getToken();
			const res = await createFetch({
				token, url, method, body,
			});
			const json = await res.json();
			const hasError = !json.ok && json.error;
			if (hasError) {
				return Promise.reject(new Error(json.error || json.message || json.toString()));
			}
			return json.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

	// Prefer the carrier id to come from the token. If it's not there, we'll use the default from our subdomain.
	// This is to support older insured portal users that may not have a carrier in their token.
	// Newer users will have one and that'll allow us to obtain the correct carrier id when working locally or in testing.
	const carrierId = getCarrierIdFromAuthData(authData) || getCarrierAndProducerIds().carrierId;

	const customerAPI = {
		get: async (id) => {
			try {
				// depending on type of id (email or actual id) we change the string at the end
				const idString = isValidEmail(id) ? `?email=${id}` : id;
				const customer = await callCoreApi({
					url: `${API_URL}/v4/customer/${idString}?carrierId=${carrierId}&withPolicies=true`,
				});

				// is all their stuff filled out?
				customer.hasFilledOutContact = getHasFilledOutContact(customer);

				// are they currently covered?
				customer.isCurrentlyCovered = CustomerHelper.isCurrentlyCovered(customer);

				customer.isOrWillBeCovered = CustomerHelper.isOrWillBeCovered(customer);

				customer.hasOnlyPomiPolicies = CustomerHelper.hasOnlyPomiPolicies(
					customer,
				);

				// are they a Vigilance Customer
				customer.isVigilanceCustomer = CustomerHelper.isAVigilanceCustomer(customer);

				return customer;
			} catch (error) {
				return Promise.reject(error);
			}
		},
		update: async (id, changes) => {
			// TODO: Omitting those customer props is probably obsolete with the v4 routes, but holding on here for now. - CC - 04/04/2024
			const body = { customer: omit(changes, CUSTOMER_PROPS_TO_OMIT) };
			body.carrierId = carrierId;
			try {
				const res = await callCoreApi({
					url: `${API_URL}/v4/customer/${id}`,
					method: 'PUT',
					body,
				});
				return res;
			} catch (error) {
				return Promise.reject(error);
			}
		},
		updateCard: async (id, paymentToken) => {
			const body = {
				carrierId,
				paymentMethod: {
					paymentToken,
				},
			};
			try {
				const res = await callCoreApi({
					url: `${API_URL}/v4/customer/${id}/payment`,
					method: 'POST',
					body,
				});
				return res;
			} catch (error) {
				return Promise.reject(error);
			}
		},
	};

	const policyAPI = {
		// This is not being used in the app, just yet. Was being used for testing allowing endorsements here, but we're doing that in the app now.
		endorse: async (policyObject) => {
		// do some cleanup
			const policyObjectToSend = omit(policyObject, [
				'noEndorse',
				'hasExclusions',
				'readOnly',
			]);
			try {
				// const policyID = policyObjectToSend.policy.id;
				// const res = await callCoreApi({
				// 	url: `http://localhost:8445/v3/policy/${policyID}`,
				// 	method: 'PUT',
				// 	body: policyObjectToSend,
				// });
				const res = policyObjectToSend;
				return res;
			} catch (error) {
				return Promise.reject(error);
			}
		},
		endorseAutoRenew: async (policyId, customerId) => {
			const body = {
				carrierId,
				customerId,
			};
			try {
				const res = await callCoreApi({
					url: `${API_URL}/v4/policy/${policyId}/endorse-auto-renew`,
					method: 'PUT',
					body,
				});
				return res;
			} catch (error) {
				return Promise.reject(error);
			}
		},
		// return type options: ['DOWNLOAD', 'FILE', 'BASE64', 'BLOB']
		download: async ({
			policyId,
			customerId,
			returnType = DOWNLOAD_TYPE.DOWNLOAD,
		}) => {
		// this little function does what it's called. Converts file blobs to bas64 encoded strings
		// found here -> https://gist.github.com/n1ru4l/dc99062577b746e0783410b1298ab897
			const convertBlobToBase64 = (blob) => new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onerror = reject;
				reader.onload = () => {
					resolve(reader.result);
				};
				reader.readAsDataURL(blob);
			});

			try {
				const token = await getToken();
				const url = `${API_URL}/v4/policy/${policyId}/download?carrierId=${carrierId}&customerId=${customerId}`;
				const file = await createFetch({ token, url, method: 'GET' });
				if (file.status !== 200) {
					return Promise.reject(
						new Error('There was an issue retrieving that policy.'),
					);
				}
				let blob;
				switch (returnType) {
					case DOWNLOAD_TYPE.BASE64:
						blob = await file.blob();
						return await convertBlobToBase64(blob);
					case DOWNLOAD_TYPE.BLOB:
						return await file.blob();
					case DOWNLOAD_TYPE.DOWNLOAD:
						blob = await file.blob();
						await saveAs(blob, `${policyId}.pdf`);
						return { ok: true };
					case DOWNLOAD_TYPE.FILE:
						return file;
					default:
						// an error occurred
						return { ok: false, error: 'downloadType not recognized.' };
				}
			} catch (error) {
				return Promise.reject(error);
			}
		},
		quote: async (fields = {}) => {
			const body = {
				...fields,
				carrierId,
			};
			const url = `${API_URL}/v4/policy/quote`;
			try {
				const res = await callCoreApi({
					url,
					method: 'POST',
					body,
				});
				return res;
			} catch (error) {
				return Promise.reject(error);
			}
		},
		updatePayment: async ({
			policyId,
			customerId,
			paymentToken,
			gateway = 'STRIPE',
			accountName = 'BUDDY',
		}) => {
			const body = {
				carrierId,
				customerId,
				paymentMethod: {
					account: accountName,
					gateway,
					paymentToken,
					paymentType: 'PAYMENT_TOKEN', // insured portal only supports payment token for now
				},
			};
			const url = `${API_URL}/v4/policy/${policyId}/payment-details`;
			try {
				const res = await callCoreApi({ url, method: 'PUT', body });
				return res;
			} catch (error) {
				return Promise.reject(error);
			}
		},
		updateRenewal: async ({
			policyId,
			customerId,
			quote,
			updates,
		}) => {
			const body = {
				carrierId,
				customerId,
				quote,
				application: {
					...updates,
				},
			};
			const url = `${API_URL}/v4/policy/${policyId}/update-renewal`;
			try {
				const res = await callCoreApi({ url, method: 'PUT', body });
				return res;
			} catch (error) {
				return Promise.reject(error);
			}
		},
		renewPolicy: async ({ policyId, customerId }) => {
			const body = { carrierId, customerId };
			const url = `${API_URL}/v4/policy/${policyId}/renew`;
			try {
				const res = await callCoreApi({ url, method: 'POST', body });
				return res;
			} catch (error) {
				return Promise.reject(error);
			}
		},
	};

	const ionApi = {
		getIon: async ({
			id,
			version,
		}) => {
			let url = `${API_URL}/v4/ion/${id}`;

			if (version) {
				url += `&version=${version}`;
			}

			try {
				const res = await callCoreApi({
					url,
				});
				return res;
			} catch (error) {
				return Promise.reject(error);
			}
		},
	};

	const contextValues = useMemo(() => ({
		customerAPI,
		ionApi,
		policyAPI,
		carrierId,
	}), []);
	return (
		<ApiContext.Provider
			value={contextValues}
		>
			{children}
		</ApiContext.Provider>
	);
};

export const useApi = () => {
	const context = useContext(ApiContext);

	if (!context) {
		throw new Error('useApi must be used within a ApiProvider');
	}

	return context;
};
